import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Card, Alert, Spinner, Row, Col, Tabs, Tab, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';
import { getPromozione, createPromozione, updatePromozione, getGeneri as fetchGeneri, getSupporti as fetchSupporti, getCaseDiProduzione as fetchCaseDiProduzione } from '../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Enum aggiornati con nuovi tipi
const TipoScontoPromozioneEnum = {
    PERCENTUALE: "PERCENTUALE",
    IMPORTO_FISSO: "IMPORTO_FISSO",
    TOTALE_FISSO: "TOTALE_FISSO",
    SPEDIZIONE_GRATIS: "SPEDIZIONE_GRATIS"
};

const TargetItemTypeEnum = {
    TUTTI: "TUTTI",
    FILM: "FILM",
    MUSICA: "MUSICA",
    COLLEZIONABILE: "COLLEZIONABILE",
    SERVIZIO: "SERVIZIO"
};

const CONDIZIONI_PROMO = [
  'Nuovo',
  'Usato - Ottime Condizioni',
  'Usato - Buone Condizioni',
  'Usato - Discrete Condizioni',
  'Usato - Accettabile',
];

function PromozioneEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tornaIndietro = useTornaIndietro('/promozioni');
  const isEditMode = id !== undefined;
  const [activeTab, setActiveTab] = useState('info');

  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    immagine_url: '',
    is_active: true,
    data_inizio: new Date(),
    data_fine: new Date(),
    tipo_sconto: TipoScontoPromozioneEnum.PERCENTUALE,
    valore_sconto: 0,
    target_item_type: TargetItemTypeEnum.TUTTI,
    target_categoria_genere: '',
    target_supporto: '',
    target_casa_di_produzione: '',
    target_brand: '',
    target_licenza: '',
    target_condizione: '',
    target_prezzo_min: null,
    target_prezzo_max: null,
    scaglioni: [],
    badge_testo: '',
    // Nuovi campi FASE 1
    importo_minimo_carrello: 0,
    quantita_minima: 1,
    primi_acquisti_solo: false,
    max_utilizzi_totali: null,
    max_utilizzi_per_cliente: null,
    priorita: 5,
    combinabile_con_altre: true,
    combinabile_con_codici: true,
    spedizione_gratuita_inclusa: false,
  });
  
  // Condizioni scelte: nel form sono caselle multiple, in formData.target_condizione una stringa "A|B"
  const condizioniSelezionate = (formData.target_condizione || '').split('|').map(s => s.trim()).filter(Boolean);
  const toggleCondizione = (condizione, attiva) => {
    const nuove = attiva ? [...condizioniSelezionate, condizione] : condizioniSelezionate.filter(c => c !== condizione);
    setFormData(prev => ({ ...prev, target_condizione: CONDIZIONI_PROMO.filter(c => nuove.includes(c)).join('|') }));
  };

  const [immagineFile, setImmagineFile] = useState(null);
  const [previewImmagine, setPreviewImmagine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // State per le opzioni dei dropdown
  const [generiOptions, setGeneriOptions] = useState([]);
  const [supportiOptions, setSupportiOptions] = useState([]);
  const [caseDiProduzioneOptions, setCaseDiProduzioneOptions] = useState([]);

  useEffect(() => {
    const loadOptions = async () => {
        try {
            const [generiRes, supportiRes, caseProdRes] = await Promise.all([
                fetchGeneri(),
                fetchSupporti(),
                fetchCaseDiProduzione()
            ]);
            setGeneriOptions(['', ...generiRes.data]);
            setSupportiOptions(['', ...supportiRes.data]);
            setCaseDiProduzioneOptions(['', ...caseProdRes.data]);
        } catch (error) {
            console.error("Errore nel caricamento delle opzioni:", error);
        }
    };

    loadOptions();

    if (isEditMode) {
      const fetchPromozione = async () => {
        try {
          const response = await getPromozione(id);
          const promozioneData = response.data;

          setFormData({
            ...promozioneData,
            data_inizio: new Date(promozioneData.data_inizio),
            data_fine: new Date(promozioneData.data_fine),
            tipo_sconto: promozioneData.tipo_sconto || TipoScontoPromozioneEnum.PERCENTUALE,
            target_item_type: promozioneData.target_item_type || TargetItemTypeEnum.TUTTI,
            target_categoria_genere: promozioneData.target_categoria_genere || '',
            target_supporto: promozioneData.target_supporto || '',
            target_casa_di_produzione: promozioneData.target_casa_di_produzione || '',
            target_brand: promozioneData.target_brand || '',
            target_licenza: promozioneData.target_licenza || '',
            target_condizione: promozioneData.target_condizione || '',
            target_prezzo_min: promozioneData.target_prezzo_min ?? null,
            target_prezzo_max: promozioneData.target_prezzo_max ?? null,
            scaglioni: promozioneData.scaglioni || [],
            badge_testo: promozioneData.badge_testo || '',
            // Nuovi campi con valori default se non presenti
            importo_minimo_carrello: promozioneData.importo_minimo_carrello || 0,
            quantita_minima: promozioneData.quantita_minima || 1,
            primi_acquisti_solo: promozioneData.primi_acquisti_solo || false,
            max_utilizzi_totali: promozioneData.max_utilizzi_totali,
            max_utilizzi_per_cliente: promozioneData.max_utilizzi_per_cliente,
            priorita: promozioneData.priorita || 5,
            combinabile_con_altre: promozioneData.combinabile_con_altre !== undefined ? promozioneData.combinabile_con_altre : true,
            combinabile_con_codici: promozioneData.combinabile_con_codici !== undefined ? promozioneData.combinabile_con_codici : true,
            spedizione_gratuita_inclusa: promozioneData.spedizione_gratuita_inclusa || false,
          });
          
          if (promozioneData.immagine_url) {
            setPreviewImmagine(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${promozioneData.immagine_url}`);
          }
          setLoading(false);
        } catch (err) {
          setError('Errore durante il caricamento della promozione.');
          setLoading(false);
        }
      };
      fetchPromozione();
    } else {
      setLoading(false);
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : 
                      (type === 'number' ? (value === '' ? null : parseFloat(value)) : value);
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleScaglioneChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      scaglioni: prev.scaglioni.map((sc, i) => i === index ? { ...sc, [field]: value } : sc)
    }));
  };

  const addScaglione = () => setFormData(prev => ({ ...prev, scaglioni: [...(prev.scaglioni || []), { qta: '', prezzo: '' }] }));

  const removeScaglione = (index) => setFormData(prev => ({ ...prev, scaglioni: prev.scaglioni.filter((_, i) => i !== index) }));

  const handleDateChange = (date, name) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };

  const handleImmagineFileChange = (e) => {
    const file = e.target.files[0];
    setImmagineFile(file);
    if (file) {
      setPreviewImmagine(URL.createObjectURL(file));
    } else {
      setPreviewImmagine(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Prepara i dati da inviare
    const payload = { ...formData };
    for (const key of ['target_categoria_genere', 'target_supporto', 'target_casa_di_produzione', 'target_brand', 'target_licenza', 'target_condizione', 'badge_testo']) {
        if (payload[key] === '') {
            payload[key] = null;
        }
    }
    // Normalizza i prezzi: stringa vuota → null, altrimenti parseFloat
    payload.target_prezzo_min = (payload.target_prezzo_min !== '' && payload.target_prezzo_min !== null && payload.target_prezzo_min !== undefined)
        ? parseFloat(payload.target_prezzo_min) : null;
    payload.target_prezzo_max = (payload.target_prezzo_max !== '' && payload.target_prezzo_max !== null && payload.target_prezzo_max !== undefined)
        ? parseFloat(payload.target_prezzo_max) : null;

    const scaglioni = payload.tipo_sconto === TipoScontoPromozioneEnum.TOTALE_FISSO
      ? (payload.scaglioni || [])
          .filter(sc => sc.qta !== '' && sc.prezzo !== '')
          .map(sc => ({ qta: parseInt(sc.qta, 10), prezzo: parseFloat(sc.prezzo) }))
          .sort((a, b) => a.qta - b.qta)
      : [];
    payload.scaglioni = scaglioni.length ? scaglioni : null;
    if (scaglioni.length) {
      payload.valore_sconto = scaglioni[0].prezzo;
      payload.quantita_minima = scaglioni[0].qta;
    }

    try {
      if (isEditMode) {
        await updatePromozione(id, payload, immagineFile);
        setSuccess('Promozione aggiornata con successo!');
      } else {
        await createPromozione(payload, immagineFile);
        setSuccess('Promozione creata con successo!');
      }
      setTimeout(() => navigate('/promozioni'), 1500);
    } catch (err) {
      console.error("Errore salvataggio promozione:", err.response ? err.response.data : err);
      setError('Errore durante il salvataggio della promozione.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p>Caricamento promozione...</p>
      </Container>
    );
  }

  const getScontoLabel = () => {
    switch(formData.tipo_sconto) {
      case TipoScontoPromozioneEnum.PERCENTUALE:
        return 'Percentuale Sconto (%)';
      case TipoScontoPromozioneEnum.IMPORTO_FISSO:
        return 'Importo Sconto Fisso (€)';
      case TipoScontoPromozioneEnum.TOTALE_FISSO:
        return 'Totale Bundle (€)';
      case TipoScontoPromozioneEnum.SPEDIZIONE_GRATIS:
        return 'Spedizione Gratuita';
      default:
        return 'Valore Sconto';
    }
  };

  return (
    <Container fluid className="d-flex flex-column" style={{ height: '100vh', padding: '1rem' }}>
      <Row className="mb-3 align-items-center">
        <Col>
          <h1 className="h3 mb-0 text-gray-800">
            {isEditMode ? `Modifica Promozione: ${formData.nome}` : 'Nuova Promozione'}
          </h1>
        </Col>
        <Col className="text-end">
          <Button variant="secondary" onClick={tornaIndietro} className="me-2">
            Torna alla Lista
          </Button>
          <Button variant="success" onClick={handleSubmit} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : (isEditMode ? 'Salva Modifiche' : 'Crea Promozione')}
          </Button>
        </Col>
      </Row>
      
      <Card className="shadow-sm">
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
            
            {/* TAB 1: Informazioni Base */}
            <Tab eventKey="info" title="Info Base">
              <Form>
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nome Promozione *</Form.Label>
                      <Form.Control
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        required
                        placeholder="Es: Saldi Estivi 2026"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stato</Form.Label>
                      <div>
                        <Form.Check
                          type="switch"
                          id="is_active"
                          name="is_active"
                          label={formData.is_active ? "Attiva" : "Disattivata"}
                          checked={formData.is_active}
                          onChange={handleChange}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Descrizione</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="descrizione"
                    value={formData.descrizione}
                    onChange={handleChange}
                    placeholder="Descrizione della promozione..."
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data Inizio *</Form.Label>
                      <DatePicker
                        selected={formData.data_inizio}
                        onChange={(date) => handleDateChange(date, 'data_inizio')}
                        dateFormat="dd/MM/yyyy HH:mm"
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        className="form-control"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data Fine *</Form.Label>
                      <DatePicker
                        selected={formData.data_fine}
                        onChange={(date) => handleDateChange(date, 'data_fine')}
                        dateFormat="dd/MM/yyyy HH:mm"
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        className="form-control"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Testo Badge</Form.Label>
                  <Form.Control
                    type="text"
                    name="badge_testo"
                    value={formData.badge_testo || ''}
                    onChange={handleChange}
                    maxLength={30}
                    placeholder="Es: Da 13,90€ — vuoto per il badge automatico"
                  />
                  <Form.Text className="text-muted">Etichetta rossa sulla card in /offerte (max 30 caratteri).</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Immagine Promozione</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleImmagineFileChange}
                    accept="image/*"
                  />
                  {previewImmagine && (
                    <div className="mt-2">
                      <img src={previewImmagine} alt="Preview" style={{ maxWidth: '200px', height: 'auto' }} />
                    </div>
                  )}
                </Form.Group>
              </Form>
            </Tab>

            {/* TAB 2: Tipo Sconto */}
            <Tab eventKey="sconto" title="Tipo Sconto">
              <Form>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Tipo di Sconto *</Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      id="sconto-percentuale"
                      name="tipo_sconto"
                      value={TipoScontoPromozioneEnum.PERCENTUALE}
                      label="Sconto Percentuale (es: 15% di sconto)"
                      checked={formData.tipo_sconto === TipoScontoPromozioneEnum.PERCENTUALE}
                      onChange={handleChange}
                      className="mb-2"
                    />
                    <Form.Check
                      type="radio"
                      id="sconto-fisso"
                      name="tipo_sconto"
                      value={TipoScontoPromozioneEnum.IMPORTO_FISSO}
                      label="Sconto Fisso in Euro (es: -5€ per prodotto)"
                      checked={formData.tipo_sconto === TipoScontoPromozioneEnum.IMPORTO_FISSO}
                      onChange={handleChange}
                      className="mb-2"
                    />
                    <Form.Check
                      type="radio"
                      id="totale-fisso"
                      name="tipo_sconto"
                      value={TipoScontoPromozioneEnum.TOTALE_FISSO}
                      label="Totale Fisso Bundle (es: 6 articoli per €20 totali)"
                      checked={formData.tipo_sconto === TipoScontoPromozioneEnum.TOTALE_FISSO}
                      onChange={handleChange}
                      className="mb-2"
                    />
                    <Form.Check
                      type="radio"
                      id="spedizione-gratis"
                      name="tipo_sconto"
                      value={TipoScontoPromozioneEnum.SPEDIZIONE_GRATIS}
                      label="Spedizione Gratuita"
                      checked={formData.tipo_sconto === TipoScontoPromozioneEnum.SPEDIZIONE_GRATIS}
                      onChange={handleChange}
                    />
                  </div>
                </Form.Group>

                {formData.tipo_sconto !== TipoScontoPromozioneEnum.SPEDIZIONE_GRATIS && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{getScontoLabel()} *</Form.Label>
                        <Form.Control
                          type="number"
                          name="valore_sconto"
                          value={formData.valore_sconto}
                          onChange={handleChange}
                          required={!(formData.tipo_sconto === TipoScontoPromozioneEnum.TOTALE_FISSO && formData.scaglioni?.length)}
                          min="0"
                          step={formData.tipo_sconto === TipoScontoPromozioneEnum.PERCENTUALE ? "0.01" : "0.01"}
                          max={formData.tipo_sconto === TipoScontoPromozioneEnum.PERCENTUALE ? "100" : undefined}
                        />
                        <Form.Text className="text-muted">
                          {formData.tipo_sconto === TipoScontoPromozioneEnum.PERCENTUALE
                            ? "Inserisci un valore tra 0 e 100"
                            : formData.tipo_sconto === TipoScontoPromozioneEnum.TOTALE_FISSO
                            ? "Prezzo totale del bundle (es: 20 per pagare €20 per N articoli)"
                            : "Inserisci l'importo in euro"}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {formData.tipo_sconto === TipoScontoPromozioneEnum.SPEDIZIONE_GRATIS && (
                  <Alert variant="info">
                    <strong>Spedizione Gratuita:</strong> Questa promozione renderà la spedizione gratuita 
                    per gli ordini che soddisfano le condizioni specificate (es: importo minimo).
                  </Alert>
                )}
                {formData.tipo_sconto === TipoScontoPromozioneEnum.TOTALE_FISSO && (
                  <Alert variant="warning">
                    <strong>Totale Fisso Bundle:</strong> Gli articoli che soddisfano il targeting
                    vengono raggruppati in bundle da <strong>N</strong> pezzi (imposta &quot;Articoli per Bundle&quot;
                    nel tab Condizioni) al prezzo totale fisso indicato sopra.<br />
                    <em>Esempio: valore = 20, bundle = 6 → 6 articoli costano €20 in totale invece della loro somma.</em>
                  </Alert>
                )}
                {formData.tipo_sconto === TipoScontoPromozioneEnum.TOTALE_FISSO && (
                  <div className="mb-3">
                    <h6>Scaglioni (opzionale)</h6>
                    <Form.Text className="text-muted d-block mb-2">
                      Con almeno uno scaglione, valore e &quot;Articoli per Bundle&quot; sopra vengono ignorati. Il carrello usa la combinazione di scaglioni più conveniente per il cliente
                      (es: 2 × 13,90 · 3 × 17,90 · 5 × 24,90). Gli articoli che non rientrano in uno scaglione restano a prezzo pieno.
                    </Form.Text>
                    {(formData.scaglioni || []).map((sc, i) => (
                      <Row key={i} className="mb-2 align-items-center">
                        <Col xs={4} md={2}>
                          <Form.Control type="number" min="1" step="1" placeholder="Articoli" value={sc.qta}
                            onChange={(e) => handleScaglioneChange(i, 'qta', e.target.value)} />
                        </Col>
                        <Col xs="auto">per €</Col>
                        <Col xs={4} md={2}>
                          <Form.Control type="number" min="0" step="0.01" placeholder="Totale" value={sc.prezzo}
                            onChange={(e) => handleScaglioneChange(i, 'prezzo', e.target.value)} />
                        </Col>
                        <Col xs="auto">
                          <Button variant="outline-danger" size="sm" onClick={() => removeScaglione(i)}>Rimuovi</Button>
                        </Col>
                      </Row>
                    ))}
                    <Button variant="outline-primary" size="sm" onClick={addScaglione}>+ Aggiungi scaglione</Button>
                  </div>
                )}
              </Form>
            </Tab>

            {/* TAB 3: Condizioni */}
            <Tab eventKey="condizioni" title="Condizioni">
              <Form>
                <h5 className="mb-3">Condizioni di Applicazione</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Importo Minimo Carrello (€)</Form.Label>
                      <Form.Control
                        type="number"
                        name="importo_minimo_carrello"
                        value={formData.importo_minimo_carrello}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                      />
                      <Form.Text className="text-muted">
                        Lasciar 0 per nessun minimo
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {formData.tipo_sconto === TipoScontoPromozioneEnum.TOTALE_FISSO
                          ? 'Articoli per Bundle'
                          : 'Quantità Minima Prodotti'}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="quantita_minima"
                        value={formData.quantita_minima}
                        onChange={handleChange}
                        min="1"
                      />
                      <Form.Text className="text-muted">
                        {formData.tipo_sconto === TipoScontoPromozioneEnum.TOTALE_FISSO
                          ? 'Quanti articoli formano un bundle (es: 6 per la promo 6x20)'
                          : 'Numero minimo di prodotti nel carrello'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    id="primi_acquisti_solo"
                    name="primi_acquisti_solo"
                    label="Valida solo per il primo acquisto del cliente"
                    checked={formData.primi_acquisti_solo}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted d-block mt-1">
                    Se attivato, solo i nuovi clienti potranno utilizzare questa promozione
                  </Form.Text>
                </Form.Group>
              </Form>
            </Tab>

            {/* TAB 4: Targeting */}
            <Tab eventKey="targeting" title="Targeting Prodotti">
              <Form>
                <h5 className="mb-3">Applicabile a...</h5>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tipo Articolo</Form.Label>
                      <Form.Select
                        name="target_item_type"
                        value={formData.target_item_type}
                        onChange={handleChange}
                      >
                        {Object.values(TargetItemTypeEnum).map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Genere/Categoria</Form.Label>
                      <Form.Select name="target_categoria_genere" value={formData.target_categoria_genere} onChange={handleChange}>
                        {generiOptions.map(g => <option key={g} value={g}>{g === '' ? 'Qualsiasi' : g}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Supporto</Form.Label>
                      <Form.Select name="target_supporto" value={formData.target_supporto} onChange={handleChange}>
                        {supportiOptions.map(s => <option key={s} value={s}>{s === '' ? 'Qualsiasi' : s}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Casa di Produzione</Form.Label>
                      <Form.Select name="target_casa_di_produzione" value={formData.target_casa_di_produzione} onChange={handleChange}>
                        {caseDiProduzioneOptions.map(c => <option key={c} value={c}>{c === '' ? 'Qualsiasi' : c}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {(formData.target_item_type === TargetItemTypeEnum.FILM || formData.target_item_type === TargetItemTypeEnum.MUSICA || formData.target_item_type === TargetItemTypeEnum.TUTTI) && (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Condizione</Form.Label>
                        {CONDIZIONI_PROMO.map(c => (
                          <Form.Check
                            key={c}
                            type="checkbox"
                            id={`condizione-${c}`}
                            label={c}
                            checked={condizioniSelezionate.includes(c)}
                            onChange={(e) => toggleCondizione(c, e.target.checked)}
                          />
                        ))}
                        <Form.Text className="text-muted">
                          Solo per Film e Musica. Nessuna casella = qualsiasi condizione; puoi sceglierne più di una.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Prezzo min (€)</Form.Label>
                      <Form.Control
                        type="number"
                        name="target_prezzo_min"
                        value={formData.target_prezzo_min ?? ''}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        placeholder="Nessun limite"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Prezzo max (€)</Form.Label>
                      <Form.Control
                        type="number"
                        name="target_prezzo_max"
                        value={formData.target_prezzo_max ?? ''}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        placeholder="Nessun limite"
                      />
                    </Form.Group>
                  </Col>
                  {formData.target_item_type === TargetItemTypeEnum.COLLEZIONABILE && (
                    <>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Brand (Collezionabili)</Form.Label>
                          <Form.Control
                            type="text"
                            name="target_brand"
                            value={formData.target_brand}
                            onChange={handleChange}
                            placeholder="Es: Funko"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Licenza (Collezionabili)</Form.Label>
                          <Form.Control
                            type="text"
                            name="target_licenza"
                            value={formData.target_licenza}
                            onChange={handleChange}
                            placeholder="Es: Star Wars"
                          />
                        </Form.Group>
                      </Col>
                    </>
                  )}
                </Row>
              </Form>
            </Tab>

            {/* TAB 5: Limiti & Avanzate */}
            <Tab eventKey="limiti" title={
              <span>
                Limiti & Avanzate 
                {isEditMode && formData.utilizzi_correnti > 0 && (
                  <Badge bg="info" className="ms-2">{formData.utilizzi_correnti}</Badge>
                )}
              </span>
            }>
              <Form>
                <h5 className="mb-3">Limiti di Utilizzo</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max Utilizzi Totali</Form.Label>
                      <Form.Control
                        type="number"
                        name="max_utilizzi_totali"
                        value={formData.max_utilizzi_totali || ''}
                        onChange={handleChange}
                        min="1"
                        placeholder="Illimitato"
                      />
                      <Form.Text className="text-muted">
                        Numero massimo di volte che questa promo può essere usata (lascia vuoto per illimitato)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max Utilizzi per Cliente</Form.Label>
                      <Form.Control
                        type="number"
                        name="max_utilizzi_per_cliente"
                        value={formData.max_utilizzi_per_cliente || ''}
                        onChange={handleChange}
                        min="1"
                        placeholder="Illimitato"
                      />
                      <Form.Text className="text-muted">
                        Numero massimo di volte che un cliente può usare questa promo
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {isEditMode && formData.utilizzi_correnti !== undefined && (
                  <Alert variant="info">
                    <strong>Utilizzi attuali:</strong> {formData.utilizzi_correnti || 0}
                    {formData.max_utilizzi_totali && ` / ${formData.max_utilizzi_totali}`}
                  </Alert>
                )}

                <hr className="my-4" />

                <h5 className="mb-3">Impostazioni Avanzate</h5>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Priorità (1-10)</Form.Label>
                      <Form.Control
                        type="number"
                        name="priorita"
                        value={formData.priorita}
                        onChange={handleChange}
                        min="1"
                        max="10"
                      />
                      <Form.Text className="text-muted">
                        Maggiore = priorità più alta
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="combinabile_con_altre"
                    name="combinabile_con_altre"
                    label="Combinabile con altre promozioni"
                    checked={formData.combinabile_con_altre}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="combinabile_con_codici"
                    name="combinabile_con_codici"
                    label="Combinabile con codici sconto"
                    checked={formData.combinabile_con_codici}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="spedizione_gratuita_inclusa"
                    name="spedizione_gratuita_inclusa"
                    label="Include la spedizione gratuita (anche sotto la soglia)"
                    checked={!!formData.spedizione_gratuita_inclusa}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Sul sito, se nel carrello si applica questa promozione la spedizione è gratuita anche se il totale è sotto la soglia
                    (vedi Impostazioni → Spedizione sito). Vale solo quando la spedizione a pagamento è attiva.
                  </Form.Text>
                </Form.Group>
              </Form>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default PromozioneEdit;
